<template>
	<ui-root v-if="loaded" :data="logInfo" :part="form" ref="frm" :errs="errors" :actions="dialogControls(env)"></ui-root>
</template>

<script setup lang="ts">
import {onMounted, onUnmounted, ref, watch, reactive} from "vue";
import {connectEventBus, injectEnv} from "@fd/Environment";
import type {LogInfo} from "@t/setup/LogInfo";
import type {WithMeta} from "@t/fd/WithMeta";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition";
import FormPart from "@fd/containers/FormPart.vue";
import type {ErrorMap} from "@fd/PartInjectKeys";
import {NetworkResponse} from "@fd/Types";
import {useRouter,useRoute} from "vue-router";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {TOOLS_GROUP_ID} from "@t/ui/Menu.ts";
import {hasAccess} from "@t/security/ObjectAccess.ts";
import UiRoot from "@fd/containers/UiRoot.vue";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {useVirtualRoute} from "@/router/virtual/VirtualRouter.ts";

const env = injectEnv();
const navStore = useNavigationStore();
const router = useRouter();
const route = useVirtualRoute();

const loaded = ref<boolean>(false);

const errors = reactive<ErrorMap>([]);
const logInfo = ref<LogInfo|null>(null);
const form = ref<InterfaceRoot<FormDefinition>|null>(null);
const bus = connectEventBus("LogInfoPage");
const frm = ref(null);

function receivedData(data:LogInfo & WithMeta):void {
	if (data.META) {
		form.value = data.META.form;
		delete data.META;
	}
	logInfo.value = data;
	loaded.value = true;
	if (logInfo.value && Boolean(logInfo.value.id)) {
		navStore.replace(logInfo.value.name);
	}
}

function loadData() {
	const id = route.query.id;
	if (id) {
		return env.net.fetch("api/loginfos/"+id+"?meta=true")
			.then((result) => result.json())
			.then(receivedData);
	} else {
		return env.net.fetch("api/loginfos/init?meta=true")
				.then((result) => result.json())
				.then(receivedData)
				.then(() =>{
					const zip = route.query.zip;
					if (zip) { // set default zip
						logInfo.value.zip=zip;
					}
				});
	}
}

function saveData():Promise<NetworkResponse> {
    return env.net.fetch("api/loginfos/"+logInfo.value.id, "PUT", logInfo.value);
}

function createData():Promise<NetworkResponse> {
    return env.net.fetch("api/loginfos", "POST", logInfo.value);
}

function sendData():Promise<NetworkResponse> {
	return Boolean(logInfo.value.id)?saveData():createData();
}

bus.onCommand(["save", "saveAndBack"], async (cmd) => {
	errors.length = 0;
	const response = await sendData();
	if (response.ok) {
		if (cmd === "save") {
			const data = await response.json();
			receivedData(data);
			if (frm.value) {
				frm.value.setDirty(false);
			}
		} else {
			// Go back
			router.back();
		}
		return "ok";
	} else {
		const data = await response.json();
		if (Array.isArray(data?.problems)) {
			for (let problem of data.problems) {
				errors.push(problem);
			}
		}
		console.error(response);
		return "fail";
	}
});

navStore.defineMenu((mb) => {
	if (logInfo.value?.id) {
		mb.inGroup(TOOLS_GROUP_ID, (gb) => {
			if (hasAccess(logInfo.value, "delete")) {
				gb.navItem("deleteLog", "deleteLog.do?id="+logInfo.value.id, "menu.deleteLog", "trash");
			}
		});
	}
});


watch(() => route.query.id, loadData, { immediate: true });
navStore.defineMenu((mb) => mb.standardMenu());

onMounted(() => {
	navStore.forward(env.msg("menu.createLog"));
});
</script>